import { JSX, useState, useCallback, KeyboardEvent, ChangeEvent } from 'react';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { ReactComponent as TagKeyEmptyStateIllustration } from '../../../assets/tagKeyEmptyState.svg';
import { ReactComponent as TagValueEmptyStateIllustration } from '../../../assets/tagValueEmptyState.svg';
import { DropdownType } from '../../../types';
import styles from '../TagAutocompleteDropdown.module.scss';
import CreateButton from './CreateButton';

interface RenderEmptyStateProps {
  dropdownType: DropdownType;
  showCreateOption: boolean;
  searchValue: string;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  confirmNewValue: () => void;
  cancelNewValue: () => void;
}

const RenderEmptyState = ({
  dropdownType,
  showCreateOption,
  searchValue,
  handleSearchChange,
  handleKeyDown,
  confirmNewValue,
  cancelNewValue,
}: RenderEmptyStateProps): JSX.Element => {
  const [isCreatingMode, setIsCreatingMode] = useState(false);

  const EmptyStateIllustration =
    dropdownType === DropdownType.KEY ? TagKeyEmptyStateIllustration : TagValueEmptyStateIllustration;

  const handleCreateButtonClick = useCallback(() => {
    setIsCreatingMode(true);
  }, []);

  const handleConfirm = useCallback(() => {
    confirmNewValue();
    setIsCreatingMode(false);
  }, [confirmNewValue]);

  const handleCancel = useCallback(() => {
    cancelNewValue();
    setIsCreatingMode(false);
  }, [cancelNewValue]);

  return (
    <div className={styles.emptyState}>
      <EmptyStateIllustration className={styles.illustration} />
      <p className={styles.emptyStateText}>
        {`Create your first Custom ${dropdownType === DropdownType.KEY ? 'Key' : 'Value'}`}
      </p>
      {showCreateOption && isCreatingMode && (
        <div className={styles.createNewContainer}>
          <div className={styles.createNewInputBox}>
            <input
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder={`Enter ${dropdownType === DropdownType.KEY ? 'Key' : 'Value'} Name`}
              className={styles.createNewInput}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
          <div className={styles.createActionButtons}>
            <button className={styles.confirmButton} onClick={handleConfirm} disabled={!searchValue.trim()}>
              <GenerateIcon iconName={ICONS.check.name} />
            </button>
            <button className={styles.cancelButton} onClick={handleCancel}>
              <GenerateIcon iconName={ICONS.xMark.name} />
            </button>
          </div>
        </div>
      )}
      {showCreateOption && !isCreatingMode && (
        <CreateButton handleCreateNew={handleCreateButtonClick} dropdownType={dropdownType} />
      )}
    </div>
  );
};

export default RenderEmptyState;
